'use client';
import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useRouter } from 'next/navigation';
import { toast } from 'sonner';
import { campaignService, Campaign, CampaignStatus } from '@/lib/supabase/campaignService';

interface CampaignFormProps {
  campaignId?: string;
}

const DEFAULT_WHATSAPP = `🎨 Campaign Name

Dear Students,

Please find the competition details in the attached banner.

Participate and win exciting prizes!

Visit: knowourindia.com`;

export default function CampaignForm({ campaignId }: CampaignFormProps) {
  const router = useRouter();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [campaign, setCampaign] = useState<Campaign | null>(null);
  const [bannerVersions, setBannerVersions] = useState<any[]>([]);
  const [bannerPreview, setBannerPreview] = useState<string | null>(null);
  const [pendingFile, setPendingFile] = useState<File | null>(null);
  const [slugManuallyEdited, setSlugManuallyEdited] = useState(false);

  const [form, setForm] = useState({
    name: '',
    slug: '',
    description: '',
    registrationUrl: '',
    whatsappMessage: DEFAULT_WHATSAPP,
    campaignStatus: 'draft' as CampaignStatus,
    startDate: '',
    endDate: '',
  });

  const isEdit = !!campaignId;

  const load = useCallback(async () => {
    if (!campaignId) return;
    setLoading(true);
    const c = await campaignService.getById(campaignId);
    if (c) {
      setCampaign(c);
      setForm({
        name: c.name,
        slug: c.slug,
        description: c.description || '',
        registrationUrl: c.registrationUrl || '',
        whatsappMessage: c.whatsappMessage || DEFAULT_WHATSAPP,
        campaignStatus: c.campaignStatus,
        startDate: c.startDate || '',
        endDate: c.endDate || '',
      });
      if (c.bannerUrl) setBannerPreview(c.bannerUrl);
      const versions = await campaignService.getBannerVersions(campaignId);
      setBannerVersions(versions);
    }
    setLoading(false);
  }, [campaignId]);

  useEffect(() => { load(); }, [load]);

  const handleNameChange = (name: string) => {
    setForm(f => ({
      ...f,
      name,
      slug: slugManuallyEdited ? f.slug : campaignService.generateSlug(name),
    }));
  };

  const handleSlugChange = (slug: string) => {
    setSlugManuallyEdited(true);
    setForm(f => ({ ...f, slug: slug.toLowerCase().replace(/[^a-z0-9-]/g, '-').replace(/-+/g, '-') }));
  };

  const handleBannerSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!['image/jpeg', 'image/png', 'image/webp'].includes(file.type)) {
      toast.error('Only JPG, PNG, or WEBP images are allowed.');
      return;
    }
    if (file.size > 10 * 1024 * 1024) {
      toast.error('Image must be under 10MB.');
      return;
    }
    setPendingFile(file);
    const url = URL.createObjectURL(file);
    setBannerPreview(url);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!form.name.trim()) { toast.error('Campaign name is required.'); return; }
    if (!form.slug.trim()) { toast.error('Campaign slug is required.'); return; }

    setSaving(true);
    try {
      let savedCampaign: Campaign | null = null;

      if (isEdit && campaign) {
        savedCampaign = await campaignService.update(campaign.id, {
          name: form.name,
          slug: form.slug,
          description: form.description,
          registrationUrl: form.registrationUrl,
          whatsappMessage: form.whatsappMessage,
          campaignStatus: form.campaignStatus,
          startDate: form.startDate || undefined,
          endDate: form.endDate || undefined,
        });
      } else {
        savedCampaign = await campaignService.create({
          name: form.name,
          slug: form.slug,
          description: form.description,
          registrationUrl: form.registrationUrl,
          whatsappMessage: form.whatsappMessage,
          campaignStatus: form.campaignStatus,
          startDate: form.startDate || undefined,
          endDate: form.endDate || undefined,
        });
      }

      if (!savedCampaign) {
        toast.error('Failed to save campaign. Slug may already be taken.');
        setSaving(false);
        return;
      }

      // Upload banner if pending
      if (pendingFile) {
        setUploading(true);
        const ok = await campaignService.replaceBanner(savedCampaign.id, pendingFile);
        setUploading(false);
        if (!ok) toast.error('Campaign saved but banner upload failed. Try replacing banner from the manage page.');
      }

      toast.success(isEdit ? 'Campaign updated!' : 'Campaign created!');
      router.push(`/campaigns/${savedCampaign.id}/qr`);
    } catch (err) {
      console.error(err);
      toast.error('An error occurred. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <div className="w-8 h-8 rounded-full border-2 border-primary border-t-transparent animate-spin" />
      </div>
    );
  }

  const campaignUrl = form.slug ? campaignService.getCampaignUrl(form.slug) : '';

  return (
    <form onSubmit={handleSubmit} className="space-y-6 max-w-2xl">
      {/* Header */}
      <div className="flex items-center gap-3">
        <button type="button" onClick={() => router.push('/campaigns')} className="btn-ghost p-2 rounded-xl">
          <svg width="18" height="18" viewBox="0 0 18 18" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round">
            <path d="M11 4L6 9L11 14" />
          </svg>
        </button>
        <div>
          <h1 className="font-bold text-foreground" style={{ fontSize: '20px' }}>
            {isEdit ? 'Edit Campaign' : 'New Campaign'}
          </h1>
          <p className="text-muted-foreground" style={{ fontSize: '12px' }}>
            {isEdit ? 'Update campaign details' : 'Fill in the details to create a new QR campaign'}
          </p>
        </div>
      </div>

      {/* Step indicator */}
      <div className="flex items-center gap-2 text-muted-foreground" style={{ fontSize: '12px' }}>
        {['Campaign Details', 'Banner & Links', 'WhatsApp Message', 'Status & Dates'].map((step, i) => (
          <React.Fragment key={step}>
            <span className="font-medium">{i + 1}. {step}</span>
            {i < 3 && <span className="text-border">→</span>}
          </React.Fragment>
        ))}
      </div>

      {/* Campaign Name */}
      <div className="bg-card border border-border rounded-2xl p-5 space-y-4">
        <h2 className="font-semibold text-foreground" style={{ fontSize: '14px' }}>Campaign Details</h2>
        <div className="space-y-1.5">
          <label className="block font-semibold text-foreground" style={{ fontSize: '13px' }}>Campaign Name *</label>
          <input
            type="text"
            className="input-field"
            placeholder="e.g. National Pride Painting Competition 2026"
            value={form.name}
            onChange={e => handleNameChange(e.target.value)}
            required
          />
        </div>
        <div className="space-y-1.5">
          <label className="block font-semibold text-foreground" style={{ fontSize: '13px' }}>Campaign Slug *</label>
          <div className="flex items-center gap-2">
            <span className="text-muted-foreground flex-shrink-0" style={{ fontSize: '13px' }}>knowourindia.com/c/</span>
            <input
              type="text"
              className="input-field flex-1"
              placeholder="painting-2026"
              value={form.slug}
              onChange={e => handleSlugChange(e.target.value)}
              required
            />
          </div>
          {form.slug && (
            <p className="text-primary font-medium" style={{ fontSize: '11px' }}>
              🔗 {campaignUrl}
            </p>
          )}
        </div>
        <div className="space-y-1.5">
          <label className="block font-semibold text-foreground" style={{ fontSize: '13px' }}>Short Description</label>
          <textarea
            className="input-field resize-none"
            rows={3}
            placeholder="Brief description of the campaign..."
            value={form.description}
            onChange={e => setForm(f => ({ ...f, description: e.target.value }))}
          />
        </div>
      </div>

      {/* Banner & Registration URL */}
      <div className="bg-card border border-border rounded-2xl p-5 space-y-4">
        <h2 className="font-semibold text-foreground" style={{ fontSize: '14px' }}>Banner & Links</h2>

        {/* Banner Upload */}
        <div className="space-y-2">
          <label className="block font-semibold text-foreground" style={{ fontSize: '13px' }}>Campaign Banner</label>
          {bannerPreview ? (
            <div className="relative rounded-2xl overflow-hidden border border-border" style={{ maxHeight: '300px' }}>
              <img src={bannerPreview} alt="Campaign banner preview" className="w-full object-contain" style={{ maxHeight: '300px' }} />
              <div className="absolute top-2 right-2 flex gap-2">
                <button
                  type="button"
                  onClick={() => { setBannerPreview(null); setPendingFile(null); if (fileInputRef.current) fileInputRef.current.value = ''; }}
                  className="bg-card border border-border rounded-xl px-3 py-1.5 font-semibold"
                  style={{ fontSize: '12px' }}
                >
                  Remove
                </button>
                <button
                  type="button"
                  onClick={() => fileInputRef.current?.click()}
                  className="btn-primary rounded-xl px-3 py-1.5 font-semibold"
                  style={{ fontSize: '12px' }}
                >
                  Replace
                </button>
              </div>
            </div>
          ) : (
            <button
              type="button"
              onClick={() => fileInputRef.current?.click()}
              className="w-full border-2 border-dashed border-border rounded-2xl p-8 flex flex-col items-center gap-3 hover:border-primary transition-colors"
            >
              <div className="w-12 h-12 rounded-2xl gradient-primary-soft flex items-center justify-center">
                <svg width="22" height="22" viewBox="0 0 22 22" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" className="text-primary">
                  <path d="M11 3v12M5 9l6-6 6 6" />
                  <path d="M3 18h16" />
                </svg>
              </div>
              <div className="text-center">
                <p className="font-semibold text-foreground" style={{ fontSize: '14px' }}>Upload Banner Image</p>
                <p className="text-muted-foreground mt-0.5" style={{ fontSize: '12px' }}>JPG, PNG or WEBP — max 10MB</p>
              </div>
            </button>
          )}
          <input
            ref={fileInputRef}
            type="file"
            accept="image/jpeg,image/png,image/webp"
            className="hidden"
            onChange={handleBannerSelect}
          />
          {isEdit && bannerVersions.length > 1 && (
            <p className="text-muted-foreground" style={{ fontSize: '11px' }}>
              {bannerVersions.length} banner versions — QR URL stays unchanged when you replace the banner.
            </p>
          )}
        </div>

        <div className="space-y-1.5">
          <label className="block font-semibold text-foreground" style={{ fontSize: '13px' }}>Registration URL</label>
          <input
            type="url"
            className="input-field"
            placeholder="https://knowourindia.com"
            value={form.registrationUrl}
            onChange={e => setForm(f => ({ ...f, registrationUrl: e.target.value }))}
          />
        </div>
      </div>

      {/* WhatsApp Message */}
      <div className="bg-card border border-border rounded-2xl p-5 space-y-3">
        <div className="flex items-center justify-between">
          <h2 className="font-semibold text-foreground" style={{ fontSize: '14px' }}>WhatsApp Sharing Message</h2>
          <span className="text-muted-foreground" style={{ fontSize: '11px' }}>{form.whatsappMessage.length} chars</span>
        </div>
        <p className="text-muted-foreground" style={{ fontSize: '12px' }}>
          This message will be shown on the campaign landing page for sharing. Users select their own WhatsApp group/contact.
        </p>
        <textarea
          className="input-field resize-none font-mono"
          rows={10}
          value={form.whatsappMessage}
          onChange={e => setForm(f => ({ ...f, whatsappMessage: e.target.value }))}
          style={{ fontSize: '13px' }}
        />
      </div>

      {/* Status & Dates */}
      <div className="bg-card border border-border rounded-2xl p-5 space-y-4">
        <h2 className="font-semibold text-foreground" style={{ fontSize: '14px' }}>Status & Dates</h2>
        <div className="space-y-1.5">
          <label className="block font-semibold text-foreground" style={{ fontSize: '13px' }}>Campaign Status</label>
          <select
            className="input-field"
            value={form.campaignStatus}
            onChange={e => setForm(f => ({ ...f, campaignStatus: e.target.value as CampaignStatus }))}
          >
            <option value="draft">Draft</option>
            <option value="active">Active</option>
            <option value="paused">Paused</option>
            <option value="archived">Archived</option>
          </select>
        </div>
        <div className="grid grid-cols-2 gap-4">
          <div className="space-y-1.5">
            <label className="block font-semibold text-foreground" style={{ fontSize: '13px' }}>Start Date (Optional)</label>
            <input type="date" className="input-field" value={form.startDate} onChange={e => setForm(f => ({ ...f, startDate: e.target.value }))} />
          </div>
          <div className="space-y-1.5">
            <label className="block font-semibold text-foreground" style={{ fontSize: '13px' }}>End Date (Optional)</label>
            <input type="date" className="input-field" value={form.endDate} onChange={e => setForm(f => ({ ...f, endDate: e.target.value }))} />
          </div>
        </div>
      </div>

      {/* Submit */}
      <div className="flex gap-3">
        <button type="button" onClick={() => router.push('/campaigns')} className="btn-ghost flex-1 py-3.5 rounded-2xl font-semibold" style={{ fontSize: '14px' }}>
          Cancel
        </button>
        <button
          type="submit"
          disabled={saving || uploading}
          className="btn-primary flex-1 py-3.5 rounded-2xl font-semibold flex items-center justify-center gap-2 disabled:opacity-70"
          style={{ fontSize: '14px' }}
        >
          {saving || uploading ? (
            <>
              <div className="w-4 h-4 rounded-full border-2 border-white border-t-transparent animate-spin" />
              {uploading ? 'Uploading Banner...' : 'Saving...'}
            </>
          ) : (
            <>
              <svg width="16" height="16" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
                <path d="M13 3L6 10L3 7" />
              </svg>
              {isEdit ? 'Save Changes' : 'Create Campaign & Generate QR'}
            </>
          )}
        </button>
      </div>
    </form>
  );
}
